import { useEffect, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import toast from 'react-hot-toast';
import { PREMIUM_SUBSCRIBE_API, PostRequestPayload } from 'constants/payloads/premiumSubscribe';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getSubscriptionItemizedTotal } from 'utils/shared/money/getSubscriptionItemizedTotal';
import { useApiGateway } from 'hooks/useApi';
import ArrowLeft from 'svg/ArrowLeft';
import CloseIcon from 'svg/CloseIcon';
import { ButtonText } from 'components/Button';
import InfoRow from './InfoRow';
import ManagePaymentCards from './ManagePaymentCards';
import { SubscriptionPayProps } from './props';

const SubscriptionPay: React.FC<SubscriptionPayProps> = ({
  className,
  onSuccess,
  onBack,
  onClose,
  coachId,
  amount,
  currency,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    post: createCoachSubscriptionPayment,
    isError: paymentError,
    error: paymentErrorMessage,
  } = useApiGateway<PostRequestPayload>(PREMIUM_SUBSCRIBE_API);

  console.log('amount', amount);

  const price = getSubscriptionItemizedTotal({ priceUnitAmount: amount });

  const handleSubmitPayment = async (paymentMethodId: string) => {
    setIsLoading(true);
    try {
      const res = await createCoachSubscriptionPayment({
        payload: {
          coachId,
          amount,
          paymentMethodId,
        },
      });

      if (res.data.success) {
        onSuccess();
        onClose?.();
      }
    } catch (err) {
      captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (paymentError) {
      toast.error(paymentErrorMessage || 'Something went wrong while processing the payment');
    }
  }, [paymentError, paymentErrorMessage]);

  return (
    <div className={className}>
      <div className="flex gap-2 ">
        {onBack && (
          <ButtonText onClick={onBack} className="text-color-brand-primary">
            <ArrowLeft className="h-5 w-5" />
          </ButtonText>
        )}
        {onClose && (
          <ButtonText onClick={onClose}>
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
        )}
        <h3 className="typography-product-heading-mobile text-color-brand-primary">
          Content subscription
        </h3>
      </div>
      <div className="typography-product-body-highlight mt-5 flex flex-col gap-3">
        <InfoRow
          label="Yearly price"
          value={`${convertUnitPriceToFormattedPrice(price?.orderSubtotal, currency).priceDisplay}`}
        />
        <InfoRow
          label="Platform Fee"
          value={`${
            convertUnitPriceToFormattedPrice(price?.platformFeeForSubscription, currency)
              .priceDisplay
          }`}
          tooltipId="platform-fee"
          tooltipContent={`A ${
            price?.platformFeeRateDecimal * 100
          }% fee is used to support our business operations and enhance your experience.`}
        />
        <InfoRow
          label="Taxes and credit cards fees"
          value={`${convertUnitPriceToFormattedPrice(price?.creditCardFee, currency).priceDisplay}`}
          tooltipId="taxes-and-credit-card-fee"
          tooltipContent={`A ${
            price?.creditCardFeeRateDecimal * 100
          }% fee is applied to cover credit card processing and applicable taxes.`}
        />
        <div className="typography-product-subheading flex justify-between gap-1 text-color-brand-primary">
          <span>Total Annual subscription</span>
          <span>{`${
            convertUnitPriceToFormattedPrice(price?.orderTotal, currency).priceDisplay
          }`}</span>
        </div>
      </div>
      <hr className="my-8" />
      <ManagePaymentCards isLoading={isLoading} handleSubmitPayment={handleSubmitPayment} />
    </div>
  );
};

export default SubscriptionPay;
